import { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { Loader2, Wrench, CheckCircle, AlertCircle, Wifi, Radar } from 'lucide-react'
import { toolsService, type Tool } from '@/services/tools'
import {
  buildMCPConfigRequest,
  buildAPIConfigRequest,
  type MCPFormData,
  type APIFormData,
  type MCPAuthType,
  type ToolConfigureRequest,
  type MCPCapability,
} from '@/schemas/tool-config'
import { MCPConfigForm } from './mcp-config-form'
import { APIConfigForm } from './api-config-form'

interface ToolConfigModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tool: Tool
  organizationId: string
  onSuccess?: () => void
}

export function ToolConfigModal({
  open,
  onOpenChange,
  tool,
  organizationId,
  onSuccess,
}: ToolConfigModalProps) {
  const { t } = useTranslation('tools')

  // State
  const [isSaving, setIsSaving] = useState(false)
  const [isTesting, setIsTesting] = useState(false)
  const [isDiscovering, setIsDiscovering] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [testResult, setTestResult] = useState<{
    success: boolean
    message: string
  } | null>(null)
  const [capabilities, setCapabilities] = useState<MCPCapability[]>([])
  const [capabilitiesMessage, setCapabilitiesMessage] = useState<string | null>(null)
  const [formValues, setFormValues] = useState<MCPFormData | APIFormData | null>(null)
  const [isFormValid, setIsFormValid] = useState(false)

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setError(null)
      setTestResult(null)
      setFormValues(null)
      setIsFormValid(false)
      setCapabilities([])
      setCapabilitiesMessage(null)
    }
  }, [open])

  // Handle form value changes
  const handleValuesChange = useCallback(
    (values: MCPFormData | APIFormData, isValid: boolean) => {
      setFormValues(values)
      setIsFormValid(isValid)
      // Clear test result when form changes
      setTestResult(null)
    },
    []
  )

  // Build config request based on tool type
  const buildConfigRequest = useCallback((): ToolConfigureRequest | null => {
    if (!formValues) return null

    if (tool.tool_type === 'mcp') {
      return buildMCPConfigRequest(formValues as MCPFormData)
    } else if (tool.tool_type === 'api') {
      return buildAPIConfigRequest(formValues as APIFormData)
    }

    return null
  }, [formValues, tool.tool_type])

  // Test connection
  const handleTestConnection = async () => {
    setError(null)
    setTestResult(null)

    const request = buildConfigRequest()
    if (!request) {
      setError(t('config.fillRequiredFields'))
      return
    }

    try {
      setIsTesting(true)
      const result = await toolsService.testToolConnection(
        organizationId,
        tool.id,
        {
          config: request.config,
          credentials: request.credentials,
        }
      )

      setTestResult({
        success: result.success,
        message: result.message,
      })
    } catch (err) {
      console.error('Test connection failed:', err)
      setTestResult({
        success: false,
        message: t('config.testFailed'),
      })
    } finally {
      setIsTesting(false)
    }
  }

  // Discover MCP capabilities
  const handleDiscoverCapabilities = async () => {
    setError(null)
    setCapabilities([])
    setCapabilitiesMessage(null)

    const request = buildConfigRequest()
    if (!request) {
      setError(t('config.fillRequiredFields'))
      return
    }

    try {
      setIsDiscovering(true)
      const result = await toolsService.discoverCapabilities(
        organizationId,
        tool.id,
        {
          config: request.config,
          credentials: request.credentials,
        }
      )

      setCapabilities(result.capabilities)
      if (result.capabilities.length > 0) {
        setCapabilitiesMessage(
          t('config.discoverSuccess', { count: result.capabilities.length })
        )
      } else {
        setCapabilitiesMessage(t('config.discoverEmpty'))
      }
    } catch (err) {
      console.error('Discover capabilities failed:', err)
      setCapabilitiesMessage(t('config.discoverFailed'))
    } finally {
      setIsDiscovering(false)
    }
  }

  // Save configuration
  const handleSave = async () => {
    setError(null)

    const request = buildConfigRequest()
    if (!request) {
      setError(t('config.fillRequiredFields'))
      return
    }

    try {
      setIsSaving(true)
      await toolsService.configureTool(organizationId, tool.id, request)
      onSuccess?.()
      onOpenChange(false)
    } catch (err) {
      console.error('Save failed:', err)
      setError(t('config.saveFailed'))
    } finally {
      setIsSaving(false)
    }
  }

  // Get default values from existing tool config
  const getDefaultValues = useCallback((): Partial<MCPFormData | APIFormData> => {
    const config = tool.config || {}

    if (tool.tool_type === 'mcp') {
      return {
        server_url: (config.server_url as string) || '',
        auth_type: (config.auth_type as MCPAuthType) || 'none',
        api_key: '', // Never show stored credentials
        username: '',
        password: '',
      }
    } else if (tool.tool_type === 'api') {
      return {
        base_url: (config.base_url as string) || '',
        api_key: '', // Never show stored credentials
        auth_header: (config.auth_header as string) || 'Authorization',
        auth_prefix: (config.auth_prefix as string) || 'Bearer',
      }
    }

    return {}
  }, [tool])

  const isBusy = isSaving || isTesting || isDiscovering

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-gray-900 border-gray-700">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Wrench className="h-5 w-5 text-cyan-500" />
            {t('config.title', { toolName: tool.name })}
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            {t('config.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Error Alert */}
          {error && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20">
              <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
              <span className="text-sm text-red-400">{error}</span>
            </div>
          )}

          {/* Test Result Alert */}
          {testResult && (
            <div
              className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                testResult.success
                  ? 'bg-emerald-500/10 border border-emerald-500/20'
                  : 'bg-amber-500/10 border border-amber-500/20'
              }`}
            >
              {testResult.success ? (
                <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
              ) : (
                <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0" />
              )}
              <span
                className={`text-sm ${
                  testResult.success ? 'text-emerald-400' : 'text-amber-400'
                }`}
              >
                {testResult.message}
              </span>
            </div>
          )}

          {/* Configuration Indicator */}
          {tool.is_configured && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <CheckCircle className="h-4 w-4 text-blue-500" />
              <span className="text-sm text-blue-400">
                {t('config.alreadyConfigured')}
              </span>
            </div>
          )}

          {/* Form based on tool type */}
          {tool.tool_type === 'mcp' && (
            <MCPConfigForm
              defaultValues={getDefaultValues() as Partial<MCPFormData>}
              onValuesChange={handleValuesChange}
            />
          )}

          {tool.tool_type === 'api' && (
            <APIConfigForm
              defaultValues={getDefaultValues() as Partial<APIFormData>}
              onValuesChange={handleValuesChange}
            />
          )}

          {tool.tool_type !== 'mcp' && tool.tool_type !== 'api' && (
            <div className="text-center py-8 text-gray-400">
              {t('config.unsupportedType')}
            </div>
          )}

          {/* Capabilities Result */}
          {capabilitiesMessage && (
            <div className="space-y-2">
              <p className="text-sm text-gray-300">{capabilitiesMessage}</p>
              {capabilities.length > 0 && (
                <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-700 bg-gray-800/50">
                  <div className="px-3 py-2 border-b border-gray-700">
                    <span className="text-xs font-medium text-gray-400 uppercase">
                      {t('config.capabilities')}
                    </span>
                  </div>
                  <ul className="divide-y divide-gray-700/50">
                    {capabilities.map((cap) => (
                      <li key={cap.name} className="px-3 py-2">
                        <p className="text-sm font-medium text-cyan-400">{cap.name}</p>
                        <p className="text-xs text-gray-500">
                          {cap.description || t('config.capabilityNoDesc')}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-gray-400"
          >
            {t('config.cancel')}
          </Button>

          {/* Discover Capabilities - MCP only */}
          {tool.tool_type === 'mcp' && (
            <Button
              variant="outline"
              onClick={handleDiscoverCapabilities}
              disabled={isBusy || !isFormValid}
              className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
            >
              {isDiscovering ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Radar className="h-4 w-4 mr-2" />
              )}
              {isDiscovering ? t('config.discovering') : t('config.discoverCapabilities')}
            </Button>
          )}

          <Button
            variant="outline"
            onClick={handleTestConnection}
            disabled={isBusy || !isFormValid}
            className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
          >
            {isTesting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Wifi className="h-4 w-4 mr-2" />
            )}
            {t('config.testConnection')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={isBusy || !isFormValid}
            className="bg-cyan-600 hover:bg-cyan-700"
          >
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {t('config.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
